import type { FunnelData } from "@hightouch/lib/llm/schema/components/schemas";
import type {
  FunnelStepGraphData,
  FunnelStepGraphDataPoint,
} from "src/pages/analytics/types";

/**
 * Transform funnel data from LLM to FunnelStepGraphData format for FunnelGraph component
 *
 * The FunnelGraph expects data in a specific format with conversion rates, bar sizes,
 * and metadata for each step. We transform the simpler LLM format into this structure.
 *
 * Supports multiple series for comparison (e.g., Male vs Female, Control vs Variant)
 */
export function transformFunnelToGraphData(
  funnelData: FunnelData,
): FunnelStepGraphData[] {
  const firstSeries = funnelData.series[0];
  if (!firstSeries) {
    return [];
  }

  // Map over the steps in the first series (all series must have same steps)
  return firstSeries.steps.map((firstSeriesStep, stepIndex) => {
    // Build the data object with all series for this step
    const data: Record<string, FunnelStepGraphDataPoint> = {};

    funnelData.series.forEach((series) => {
      const step = series.steps[stepIndex];
      if (step) {
        data[series.name] = {
          groupByValue: undefined, // No grouping for LLM funnels
          count: step.value,
          conversion: step.conversionRate,
          conversionBarSize: step.conversionRate,
          dropOffBarSize: 1 - step.conversionRate,
        };
      }
    });

    return {
      // Used by FunnelGraph for display
      index: stepIndex,
      stageName: firstSeriesStep.name,

      // Data for all series at this step
      data,

      // Required by type but unused for LLM-generated funnels
      // (these are used for creating audiences from funnel steps)
      eventModelId: "",
      relationshipId: "",
      subconditions: [],
    };
  });
}
